import type { MissionDto, ShipDto, BuildingDto, DefenseStructureDto, ForgeActiveResearch, PlayerRaidDto, ChantierOpenTierDto } from '@/api/hooks'

/**
 * Unifies everything with a build/resolve timer into one "in progress" feed for
 * the dashboard: missions, PvP raids in flight, building upgrades, ship
 * construction, and Forge research (timed async job since the async-research feature).
 */
export type InProgressCategory = 'mission' | 'pvp_raid' | 'building' | 'defense' | 'ship' | 'forge' | 'chantier'

export interface InProgressItem {
  id: string
  category: InProgressCategory
  title: string
  subtitle?: string
  /** ISO timestamp the timer resolves at — drives the countdown. */
  resolvesAt: string
  /** Carried only for missions, so grid mode can render the rich MissionCard. */
  mission?: MissionDto
  /** Ship type key for the icon (ships + missions). */
  shipType?: string
  /** Building type key for the icon (building upgrades). */
  buildingType?: string
  /** Defence structure key for the icon (defence upgrades). */
  defenseType?: string
  /** Forge branch key for the icon (forge research). */
  forgeBranch?: string
  /** Way taken on a Grand Chantier tier ('or' | 'bois'), for the subtitle. */
  chantierPath?: string
}

export interface InProgressGroup {
  category: InProgressCategory
  label: string
  items: InProgressItem[]
}

const CATEGORY_ORDER: InProgressCategory[] = ['mission', 'pvp_raid', 'building', 'defense', 'ship', 'forge', 'chantier']
const CATEGORY_LABELS: Record<InProgressCategory, string> = {
  mission: 'Missions',
  pvp_raid: 'Raids PvP',
  building: 'Bâtiments',
  defense: 'Défenses',
  ship: 'Chantier naval',
  forge: 'Forge',
  // PAS 'Chantier' tout court : la catégorie 'ship' s'appelle déjà « Chantier naval ».
  chantier: 'Grand Chantier',
}

/**
 * A cancelled mission whose fleet hasn't landed yet: the backend keeps the ships
 * `in_mission` and only sets `completedAt` once the delayed "return" message
 * fires at `resolvesAt` (recomputed to the return-trip arrival time on cancel).
 * Until then the mission is a homebound trip in progress, not a dead entry —
 * the front must keep showing it (countdown + fleet) or the ships look like
 * they vanished. Once `completedAt` is set the mission has landed and drops out.
 */
export function isReturningMission(m: MissionDto): boolean {
  return m.status === 'cancelled' && m.completedAt == null && !!m.resolvesAt
}

/** Flattens the timered sources into a single list of in-progress items. */
export function collectInProgress(
  missions: ReadonlyArray<MissionDto>,
  ships: ReadonlyArray<ShipDto>,
  buildings: ReadonlyArray<BuildingDto>,
  defenses: ReadonlyArray<DefenseStructureDto> = [],
  forge: ForgeActiveResearch | null = null,
  pvpRaids: ReadonlyArray<PlayerRaidDto> = [],
  /** Le palier du Grand Chantier dont la maçonnerie tourne (jamais celui qui prend encore des versements). */
  chantier: ChantierOpenTierDto | null = null,
): InProgressItem[] {
  const items: InProgressItem[] = []

  for (const m of missions) {
    if (m.status === 'in_progress' && m.resolvesAt) {
      items.push({
        id: `mission-${m.id}`,
        category: 'mission',
        title: m.typeLabel,
        subtitle: m.destinationIslandName ?? m.shipName ?? undefined,
        resolvesAt: m.resolvesAt,
        mission: m,
        shipType: m.shipType ?? undefined,
      })
    } else if (isReturningMission(m)) {
      // Cancelled but still at sea: the fleet is sailing home, `resolvesAt` now
      // holds the return-arrival time. Title says "Retour" so it reads as a
      // homebound trip, not a stuck/bugged mission.
      items.push({
        id: `mission-${m.id}`,
        category: 'mission',
        title: `Retour · ${m.typeLabel}`,
        subtitle: m.destinationIslandName ?? m.shipName ?? undefined,
        resolvesAt: m.resolvesAt,
        mission: m,
        shipType: m.shipType ?? undefined,
      })
    }
  }

  // Outgoing PvP raids in flight — a separate entity from missions, so they need
  // their own pass (target island as title, scheduledAt drives the countdown).
  for (const r of pvpRaids) {
    if (r.status === 'traveling' && r.scheduledAt) {
      items.push({
        id: `pvp-raid-${r.id}`,
        category: 'pvp_raid',
        title: r.defenderIslandName ? `Raid sur ${r.defenderIslandName}` : 'Raid PvP',
        subtitle: r.defenderPseudo ?? undefined,
        resolvesAt: r.scheduledAt,
      })
    }
  }

  for (const b of buildings) {
    if (b.isUpgrading && b.upgradeResolvesAt) {
      items.push({
        id: `building-${b.id}`,
        category: 'building',
        title: b.typeLabel,
        subtitle: `Niveau ${b.level} → ${b.level + 1}`,
        resolvesAt: b.upgradeResolvesAt,
        buildingType: b.type,
      })
    }
  }

  for (const d of defenses) {
    if (d.isUpgrading && d.upgradeResolvesAt) {
      items.push({
        id: `defense-${d.type}`,
        category: 'defense',
        title: d.label,
        subtitle: `Niveau ${d.level} → ${d.level + 1}`,
        resolvesAt: d.upgradeResolvesAt,
        defenseType: d.type,
      })
    }
  }

  for (const s of ships) {
    if (s.status === 'under_construction' && s.constructionResolvesAt) {
      items.push({
        id: `ship-${s.id}`,
        category: 'ship',
        title: s.name,
        subtitle: s.shipTypeLabel,
        resolvesAt: s.constructionResolvesAt,
        shipType: s.shipType,
      })
    }
    if (s.status === 'refitting' && s.refitResolvesAt) {
      items.push({
        id: `refit-${s.id}`,
        category: 'ship',
        title: s.name,
        subtitle: s.refit ? `Refonte (${s.refit.label})` : 'Refonte',
        resolvesAt: s.refitResolvesAt,
        shipType: s.shipType,
      })
    }
  }

  if (forge) {
    items.push({
      id: `forge-${forge.techKey}`,
      category: 'forge',
      title: forge.techLabel,
      subtitle: forge.branchLabel,
      resolvesAt: forge.resolvesAt,
      forgeBranch: forge.branch,
    })
  }

  // Le Grand Chantier n'apparaît qu'une fois la maçonnerie lancée : une
  // souscription qui prend encore des versements n'a pas d'échéance à afficher.
  if (chantier && chantier.masonryStarted && chantier.resolvesAt) {
    items.push({
      id: `chantier-${chantier.tierKey}`,
      category: 'chantier',
      title: chantier.tierLabel,
      subtitle: chantier.pathLabel,
      resolvesAt: chantier.resolvesAt,
      chantierPath: chantier.path,
    })
  }

  return items
}

/** Groups items by category in a stable order, dropping empty categories. */
export function groupInProgress(items: InProgressItem[]): InProgressGroup[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    items: items.filter((i) => i.category === category),
  })).filter((g) => g.items.length > 0)
}
